/* eslint-disable max-lines */
import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { EntityStore } from '@adornis/baseql/client/entityStore.js';
import { Arg, Entity, Field, Mutation, Query } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { _subscribeCurrentUser, updateCurrentUser } from '@adornis/users/client/currentUser.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { AdornisUser, loginWithToken } from '@adornis/users/db/a-user.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { emailRegexCheck, emailValidation } from '@adornis/users/db/validators.js';
import { assertUserFromContext } from '@adornis/users/server/util.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { and } from '@adornis/validation/functions/and.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { ValidationFunctionOptions } from '@adornis/validation/validator.js';
import { TimeSlot } from '@ils/evaluation-scheduler/db/time-slot.js';
import { ilsRecipients } from '@ils/mails/recipients.js';
import { mailer } from '@ils/mails/server/mailer.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import { hasRole } from '@ils/permissions/hasRole.js';
import {
  FederalStatesGermany,
  Gender,
  UserRole,
  type ExamType,
  type OSCECategory,
  type Specialization,
} from '@ils/static/enums.js';
import { Names } from '@ils/static/names.js';
import { DateTime } from 'luxon';
import * as qrcode from 'qrcode';
import { isProduction } from '../shared/util.js';
import { RegisterMailSentEventLog } from './eventlog.js';
import { PasswordResetMail, WelcomeEmail } from './mails/userMails.js';
import { Order } from './order.js';
import { PaymentsData } from './payments-data.js';
import { ILSToken } from './token.js';
import { getSalutation } from './utils.js';

@Entity()
export class ILSUserMailState extends AdornisEntity {
  static override _class = 'SAMUserMailState';

  @Field(type => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  @validate(nonOptional())
  @Field(type => ID)
  orderID!: string;

  @DataLoadedField(type => Order, ({ orderID }) => orderID)
  order!: Order;

  @Field(type => String)
  certificate!: string;

  @Field(type => DateTime)
  lastSent: Maybe<DateTime>;

  @Field(type => DateTime)
  firstSent: Maybe<DateTime>;
}

@Entity()
export class ILSUserResults extends ILSUserMailState {
  static override _class = 'SAMUserResults';

  @Field(type => ID)
  resultID: Maybe<string>;
}

@Entity()
export class ILSProfile extends AdornisEntity {
  static override _class = 'SAMProfile';

  @validate(({ value, key, target, source }: ValidationFunctionOptions<string, ILSProfile>) => {
    if (source?._class !== Names.ILSUser._class || !hasRole(source as ILSUser, UserRole.TRAINEE)) return;
    if (target.targetFederalState === FederalStatesGermany.BAYERN) return;
    if (!value)
      throw new ValidationError('B2 Zertifikat muss angegeben werden', {
        key,
        instance: target,
        translationKey: 'certificate',
      });
  })
  @Field(type => ID)
  b2Certificate!: string;

  @validate(({ value, key, target, source }: ValidationFunctionOptions<string, ILSProfile>) => {
    if (source?._class !== Names.ILSUser._class || !hasRole(source as ILSUser, UserRole.TRAINEE)) return;
    if (!value)
      throw new ValidationError('Bitte geben sie einen Geburtsort an.', {
        key,
        instance: target,
        translationKey: 'validation_non_optional_birtplace',
      });
  })
  @Field(type => String)
  birthplace!: string;

  @validate(({ value, key, target, source }: ValidationFunctionOptions<string, ILSProfile>) => {
    if (source?._class !== Names.ILSUser._class || !hasRole(source as ILSUser, UserRole.TRAINEE)) return;
    if (!value)
      throw new ValidationError('Bitte geben sie ein Geburtsland an.', {
        key,
        instance: target,
        translationKey: 'validation_non_optional_birth_country',
      });
  })
  @Field(type => String)
  birthCountry!: string;

  @validate(({ value, key, target, source }: ValidationFunctionOptions<Maybe<DateTime>, ILSProfile>) => {
    if (source?._class !== Names.ILSUser._class || !hasRole(source as ILSUser, UserRole.TRAINEE)) return;
    if (!value)
      throw new ValidationError('Bitte geben sie ein Geburtsdatum an.', {
        key,
        instance: target,
        translationKey: 'validation_non_optional_dateOfBirth',
      });
    if (value > DateTime.now().minus({ years: 18 }))
      throw new ValidationError('Sie müssen 18 Jahre alt sein.', {
        key,
        instance: target,
        translationKey: 'validation_non_optional_dateOfBirth',
      });
  })
  @Field(type => DateTime)
  dateOfBirth: Maybe<DateTime>;

  @validate(({ value, key, target, source }: ValidationFunctionOptions<Maybe<Gender>, ILSProfile>) => {
    if (source?._class !== Names.ILSUser._class || !hasRole(source as ILSUser, UserRole.TRAINEE)) return;
    if (!value)
      throw new ValidationError(`Bitte wählen Sie ein Geschlecht aus.`, {
        key,
        instance: target,
        translationKey: 'gender',
      });
    if (!Object.values(Gender).includes(value))
      throw new ValidationError(`${value} ist kein gültiges Geschlecht.`, {
        key,
        instance: target,
        translationKey: 'gender',
      });
  })
  @Field(type => String)
  gender!: Gender;

  // save print-result pdfs and when they were sent as mails via this platform
  @Field(type => [ILSUserResults], { default: v => v ?? [] })
  resultsEmailState!: ILSUserResults[];

  @Field(type => [ILSUserMailState], { default: v => v ?? [] })
  certificateEmailState: Maybe<ILSUserMailState[]>;

  @validate(({ value, key, target, source }) => {
    if (source?._class !== Names.ILSUser._class || !hasRole(source as ILSUser, UserRole.TRAINEE)) return;
    if (!value) throw new ValidationError('Bitte geben Sie ein Bundesland an', { key, target });
    if (!Object.values(FederalStatesGermany).includes(value))
      throw new ValidationError('Es können nur deutsche Bundesländer ausgewählt werden', { key, target });
  })
  @Field(type => String)
  /**
   * Das Bundesland für das die Prüfungs abgelegt werden soll, hat aktuell nur Auswirkungen auf die Angabe eines B2-Zertifikats beim Registrieren
   */
  targetFederalState: Maybe<FederalStatesGermany>;

  @Field(type => Boolean)
  hasParticipatedExam: Maybe<boolean>;

  @Field(type => Boolean)
  participatedC1Course: Maybe<boolean>;

  @Field(type => Boolean, { default: v => v ?? false })
  wantsNewsletter!: boolean;
}

@Entity()
export class ILSUser extends AdornisUser {
  static override _class = Names.ILSUser._class;

  static override get allFields() {
    return selectionSet(() => this, 4);
  }

  constructor(data: PartialEntityData<ILSUser>) {
    super(data);
  }

  @Field(type => PaymentsData, { default: v => v ?? new PaymentsData({}) })
  ecommerce?: PaymentsData;

  @Field(type => String)
  coInvoice?: string;

  @Field(type => ILSProfile, { default: v => v ?? new ILSProfile({}) })
  profile?: ILSProfile;

  @Field(type => [String], { default: v => v ?? [] })
  assignedOSCETypes?: OSCECategory[];

  @Field(type => [TimeSlot], { default: v => v ?? [] })
  openTimeslots!: TimeSlot[];

  @validate(({ value, key, target }) => {
    if (!hasRole(target, UserRole.INSPECTOR)) return;
    if (!value) throw new ValidationError('Spezialisierung ist erforderlich', { key, target });
  })
  @Field(type => String)
  specialization!: Specialization;

  static override emailValidator: (options: ValidationFunctionOptions<string, any>) => void = and(
    nonOptional(),
    emailValidation,
  );

  /** @deprecated use standalone getSalutation function */
  getSalutation() {
    return getSalutation(this);
  }

  static async loginILS(shortToken: string) {
    if (!A.isClient) throw new Error('loginILS can only be called on client');

    const token = await this.requestILSLoginToken(shortToken)(ILSToken.allFields);
    const [userID, userClass] = await loginWithToken(token.loginToken)();
    updateCurrentUser(undefined, false);
    await _subscribeCurrentUser({ userID, userClass });
    return token;
  }

  override async remove() {
    if (A.isClient) EntityStore.drop(this);
    return ILSUser.removeILSUser(this._id)();
  }

  @Mutation(type => Boolean)
  static removeILSUser(@Arg('userID', type => ID) userID: string) {
    return async () => {
      const removeResult = await this.removeEntity(this._class, userID)();

      // cleanup candidate references
      const examdayCollection = await getRawCollection<{ _id: string }>(Names.ExamDay._collectionName);
      await examdayCollection.updateMany({ candidateIDs: userID }, { $pull: { candidateIDs: userID } });

      // cleanup tester references
      await examdayCollection.updateMany({ testers: userID }, { $pull: { testers: userID } });
      await examdayCollection.updateMany({ fallbackTesters: userID }, { $pull: { fallbackTesters: userID } });

      // cleanup slots and answers for trainees
      const examslotCollection = await getRawCollection(Names.Slot._collectionName);
      await examslotCollection.updateMany({ candidateID: userID }, { $set: { candidateID: '<REMOVED_USER>' } });
      // cleanup slots for testers
      await examslotCollection.updateMany({ tester: userID }, { $set: { tester: '<REMOVED_USER>' } });

      return removeResult;
    };
  }

  @Query(type => [ILSUser])
  static getUsersSortedByLastname(@Arg('userIDs', type => [String]) userIDs: string[]) {
    return async (gqlFields: BaseQLSelectionSet<ILSUser>) => {
      const collection = await getCollection<ILSUser>(ILSUser._class);
      return collection
        .find({ _id: { $in: userIDs } })
        .sort({ 'ecommerce.lastName': 1 })
        .toArray() as unknown as ILSUser[];
    };
  }

  @Query(type => Boolean)
  static sendPasswordResetMail(@Arg('email', type => String) email: string) {
    return async () => {
      if (!emailRegexCheck(email)) throw new Error('The email is not valid.');

      const userColl = await getRawCollection<ILSUser>(Names.ILSUser._collectionName);
      const user = await userColl.findOne(
        { email },
        {
          projection: {
            _id: 1,
            _class: 1,
            email: 1,
            username: 1,
            ecommerce: { firstName: 1, lastName: 1 },
            profile: { gender: 1 },
          },
        },
      );
      if (!user) return false;

      // TODO how to theme this? if at all...
      await PasswordResetMail(user).send({ mailer, recipients: ilsRecipients(user.email) });

      return true;
    };
  }

  @Mutation(type => String)
  static registerILSUser(
    @Arg('email', type => String) email: string,
    @Arg('password', type => String) password: string,
    @Arg('ecommerce', type => PaymentsData) ecommerce?: PaymentsData,
    @Arg('profile', type => ILSProfile) profile?: PartialEntityData<ILSProfile>,
    @Arg('examType', type => String) examType?: ExamType,
  ) {
    return async () => {
      const userID = await this.registerUser({
        username: A.getGloballyUniqueID(),
        email,
        password,
        additionalFields: {
          ecommerce,
          profile,
          roles: [new AdornisRoleToContextsWrapper({ name: UserRole.TRAINEE, contexts: [GLOBAL_CONTEXT] })],
        },
      });
      await this._sendRegistrationMail(email, ecommerce?.firstName ?? '---', ecommerce?.lastName ?? '---', examType);
      return userID;
    };
  }

  resultMailSent(orderID: string) {
    if (!orderID) throw new Error('user.resultMailSent: orderID not set');
    return !!this.profile?.resultsEmailState.find(state => state.orderID === orderID)?.resultID;
  }

  certificateMailSent(orderID: string): boolean {
    if (!orderID) throw new Error('user.certificateMailSent: orderID not set');
    return !!this.profile?.certificateEmailState?.find(state => state.orderID === orderID)?.certificate;
  }

  static resultMailSentSelection = {
    _id: 1,
    profile: { _class: 1, resultsEmailState: { _class: 1, resultID: 1, orderID: 1 } },
  };

  /** @deprecated internal and test use only */
  static _disableRegistrationMails = false;
  private static async _sendRegistrationMail(email: string, firstName: string, lastName: string, examType?: ExamType) {
    if (!A.isServer) throw new Error('Only call this serverside!');
    if (this._disableRegistrationMails) return;

    await WelcomeEmail(firstName, lastName, examType).send({
      mailer,
      recipients: ilsRecipients(email),
    });

    await new RegisterMailSentEventLog({ recipient: email }).create();
  }

  @Query(type => ILSToken, { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } })
  static requestILSLoginToken(@Arg('shortToken', type => String) shortToken: string) {
    return async (gqlFields: BaseQLSelectionSet<ILSToken>) => {
      // needs to be raw collection, because this tokens logs you in
      const collection = await getRawCollection<ILSToken>(ILSToken._collectionName);
      const token = await collection.findOne({ shortToken });
      if (!token) throw new Error('shortToken not valid.');
      return token;
    };
  }

  @Query(type => [ILSUser])
  static getUsersByRoles(@Arg('roles', type => [String]) roles: string[]) {
    return async (gqlFields: BaseQLSelectionSet<ILSUser>) => {
      if (!context.serverContext && !context.userID)
        throw new Error('You dont have the permission to list users or no User is attached to context');
      const collection = await getCollection<ILSUser>(ILSUser._class);
      return collection.find({ 'roles.name': { $in: roles } }).toArray() as unknown as ILSUser[];
    };
  }

  @Query(type => [ILSUser])
  static getUserIDsByRole(@Arg('roles', type => [String]) roles: string[]) {
    return async (gqlFields: BaseQLSelectionSet<ILSUser>) => {
      const collection = await getRawCollection<ILSUser>(this._collectionName);
      return collection
        .find({ 'roles.name': { $in: roles } }, { projection: { _id: 1, _class: 1 } })
        .toArray() as unknown as ILSUser[];
    };
  }

  missingProperties() {
    if (!this.profile?.targetFederalState) return ['profile.targetFederalState'];
    if (this.profile?.targetFederalState !== FederalStatesGermany.BAYERN && !this.profile?.b2Certificate)
      return ['profile.b2Certificate'];
    return [];
  }

  @Query(type => Boolean)
  static getEmailExists(@Arg('email', type => String) email: string) {
    return async () => {
      const col = await getRawCollection<ILSUser>(this._collectionName);
      return !!(await col.countDocuments({ email }));
    };
  }

  @Mutation(type => String)
  static generate2FAQRCode(@Arg('userID', type => String) userID: string) {
    // eslint-disable-next-line complexity
    return async () => {
      if (!context.serverContext && !context.userID)
        throw new Error('No authorization to generate 2fa code for this user');
      const user = context.serverContext
        ? null
        : await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
      if (!user || (!context.serverContext && user._id !== userID && !hasPermission(user, 'users', 'modify')))
        throw new Error('No authorization to generate 2fa code for this user');

      const { default: twofactor } = await import('node-2fa');
      const col = await getCollection<ILSUser>(this._class);
      const targetUser = await col.findOne({ _id: userID });

      if (!targetUser) throw new Error('No authorization to generate 2fa code for this user');
      if (targetUser['2fa-secret']) return '';

      const newSecret = twofactor.generateSecret({
        name: 'FaMed' + isProduction ? '' : ` ${baseConfig.get('DEPLOYMENT')}`,
        account: targetUser.email,
      });
      await col.updateOne({ _id: userID }, { $set: { '2fa-secret': newSecret.secret } });

      return qrcode.toDataURL(newSecret.uri);
    };
  }

  @Mutation(type => Boolean)
  static updateAssignedOSCETypes(
    @Arg('userID', type => ID) userID: string,
    @Arg('osceTypes', type => [String]) osceTypes: string[],
  ) {
    return async (gqlFields: void) => {
      const currentUser = await assertUserFromContext<ILSUser>({
        _id: 1,
        roles: { name: 1, contexts: 1 },
      });
      // TODO[permsrefine] do we need modifyassignedosces here?
      if (!hasPermission(currentUser, 'users', 'modify')) throw new Error('Not allowed to update assigned OSCETypes');

      const user = await ILSUser.getByID<ILSUser>(userID)({ _id: 1, _class: 1, assignedOSCETypes: 1 });
      if (!user) {
        throw new Error('User not found');
      }

      const col = await getRawCollection<PartialEntityData<ILSUser>>(this._collectionName);
      await col.updateOne({ _id: userID }, { $set: { assignedOSCETypes: osceTypes } });

      return true;
    };
  }
}
